import type { ReactElement } from 'react';

import { generateId } from '@commons/helpers/string';
import { getObjectKeys, notEmpty } from '@commons/helpers/typescript';
import type { Page } from '@commons/services/page';
import type {
  DrupalBlock,
  DrupalNode,
  DrupalParagraph,
  DrupalSection,
  DrupalTerm,
  DrupalView,
} from '@commons/types/drupal';

import type { BlocksFactory } from './blocks';
import BlockWrapper from './blocks/Wrapper';
import type { NodesFactory } from './nodes';
import type { ParagraphsFactory } from './paragraphs';
import type { SectionsFactory } from './sections';
import SectionWrapper from './sections/Wrapper';
import type { TermsFactory } from './terms';
import type { ViewsFactory } from './views';
import type { WebformProps, WebformsFactory } from './webforms';

// Type definition for an object containing factories to register in the PageBuilder.

type FactoriesToRegister = {
  sections?: SectionsFactory;
  blocks?: BlocksFactory;
  nodes?: NodesFactory;
  paragraphs?: ParagraphsFactory;
  terms?: TermsFactory;
  views?: ViewsFactory;
  webforms?: WebformsFactory;
};

/**
 * The PageBuilder instance handle registration and rendering of Drupal entities.
 *
 * It also stores the current page data to be used in the components through a getter and setter.
 *
 * It has a render method for each type of entity, such as {@link PageBuilder.renderBlock}, {@link PageBuilder.renderSection}, or {@link PageBuilder.renderNode}.
 * Other helper methods are also available (e.g. {@link PageBuilder.renderBlock}, {@link PageBuilder.sortBlocksByWeight}, {@link PageBuilder.filterBlocksByRegions}).
 *
 * To register custom entities in a package, a factory should be passed to the {@link PageBuilder.registerFactories} method.
 *
 * IMPORTANT:
 * - The factories should never be imported directly in this file, as it would create a circular dependency.
 *   This would cause the site to crash randomly in production through race conditions.
 */

const PageBuilder = {
  /**
   * Page variable to store the current page data set in the PageBuilder instance.
   *
   * It can be accessed anywhere through the {@link PageBuilder.getPage} method.
   */
  page: undefined as Page | undefined,

  /**
   * Get the current page data set in the PageBuilder instance.
   *
   * A generic type can be passed to cast the page properties with node specific props.
   *
   * @returns The current page data or undefined if not set.
   */
  getPage: <NodeProps,>() => {
    return PageBuilder.page as (Page & NodeProps) | undefined;
  },

  /**
   * Set the current page data in the PageBuilder instance.
   *
   * It can be accessed anywhere through the {@link PageBuilder.getPage} method.
   *
   * @param newPage - The new page data to set.
   *
   * IMPORTANT:
   * - This should be called once on the server and client of each route, otherwise it will be undefined.
   */
  setPage: (newPage?: Page) => {
    if (newPage) {
      PageBuilder.page = newPage;
    }
  },

  /**
   * Pagebuilder factory of Drupal sections, extended through the {@link PageBuilder.registerFactories} method.
   */
  sections: {} as SectionsFactory,

  /**
   * Pagebuilder factory of Drupal blocks, extended through the {@link PageBuilder.registerFactories} method.
   */
  blocks: {} as BlocksFactory,

  /**
   * Pagebuilder factory of Drupal nodes, extended through the {@link PageBuilder.registerFactories} method.
   */
  nodes: {} as NodesFactory,

  /**
   * Pagebuilder factory of Drupal paragraphs, extended through the {@link PageBuilder.registerFactories} method.
   */
  paragraphs: {} as ParagraphsFactory,

  /**
   * Pagebuilder factory of Drupal terms, extended through the {@link PageBuilder.registerFactories} method.
   */
  terms: {} as TermsFactory,

  /**
   * Pagebuilder factory of Drupal views, extended through the {@link PageBuilder.registerFactories} method.
   */
  views: {} as ViewsFactory,

  /**
   * Pagebuilder factory of Drupal webforms, extended through the {@link PageBuilder.registerFactories} method.
   */
  webforms: {} as WebformsFactory,

  /**
   * Register the factories of Drupal entities that can be rendered.
   *
   * @param factoriesToRegister - An object containing factories to register.
   *
   * IMPORTANT:
   * - This should be called once on the server and client of each package, typically through the root layout so it is done before any rendering.
   * - The factories should never be imported directly in this file, as it would create a circular dependency.
   *   This would cause the site to crash randomly in production through race conditions.
   * - Unless you know what you are doing, it should at least include the factories defined in `@commons/pagebuilder/*`.
   */
  registerFactories: (factoriesToRegister: FactoriesToRegister = {}) => {
    if (factoriesToRegister.sections) {
      const sectionsToRegister = factoriesToRegister.sections;
      getObjectKeys(sectionsToRegister).forEach((id) => {
        PageBuilder.sections[id] = sectionsToRegister[id];
      });
    }

    if (factoriesToRegister.blocks) {
      const blocksToRegister = factoriesToRegister.blocks;
      getObjectKeys(blocksToRegister).forEach((id) => {
        PageBuilder.blocks[id] = blocksToRegister[id];
      });
    }

    if (factoriesToRegister.nodes) {
      const nodesToRegister = factoriesToRegister.nodes;
      getObjectKeys(nodesToRegister).forEach((id) => {
        PageBuilder.nodes[id] = nodesToRegister[id];
      });
    }

    if (factoriesToRegister.paragraphs) {
      const paragraphsToRegister = factoriesToRegister.paragraphs;
      getObjectKeys(paragraphsToRegister).forEach((id) => {
        PageBuilder.paragraphs[id] = paragraphsToRegister[id];
      });
    }

    if (factoriesToRegister.terms) {
      const termsToRegister = factoriesToRegister.terms;
      getObjectKeys(termsToRegister).forEach((id) => {
        PageBuilder.terms[id] = termsToRegister[id];
      });
    }

    if (factoriesToRegister.views) {
      const viewsToRegister = factoriesToRegister.views;
      getObjectKeys(viewsToRegister).forEach((id) => {
        PageBuilder.views[id] = viewsToRegister[id];
      });
    }

    const webformsToRegister = factoriesToRegister.webforms;
    if (webformsToRegister) {
      getObjectKeys(webformsToRegister).forEach((id) => {
        PageBuilder.webforms[id] = webformsToRegister[id];
      });
    }
  },

  /**
   * Render a section based on its type.
   *
   * @param section - A section payload from Drupal.
   *
   * @returns The rendered section.
   */
  renderSection: (section: DrupalSection) => {
    const Section = PageBuilder.sections[section.type];
    if (!Section) return null;

    const id = generateId(`${section.type}-${section.weight}`);

    return (
      <SectionWrapper attributes={section.attributes} key={id}>
        <Section {...section} key={id} />
      </SectionWrapper>
    );
  },

  /**
   * Render a region with its props.
   *
   * In editor mode, this method is overriden to enable drag and drop.
   *
   * @param name - The name of the region.
   * @param Region - The region component.
   * @param props - The props of the region.
   * @param section - The section data.
   *
   * @returns The rendered region.
   */
  renderRegion: <P extends object>(
    name: string,
    Region: (props: P) => ReactElement,
    props: P & { children: React.ReactNode },
    section: Partial<DrupalSection>
  ) => {
    const id = generateId(`${section.type}-${section.weight}-${name}`);
    return <Region {...props} key={id} />;
  },

  /**
   * Given an array of blocks, sort them by weight and remove null blocks.
   *
   * @param blocks - An array of blocks or null value.
   *
   * @returns Sorted array of blocks.
   */
  sortBlocksByWeight: (blocks: (DrupalBlock | null)[]) => {
    const filteredBlocks = blocks.filter((block) => block !== null) as DrupalBlock[];
    return filteredBlocks.sort((a, b) => a.weight - b.weight);
  },

  /**
   * Given an array of blocks or null, returns an object with the blocks grouped by region.
   *
   * Also sort the blocks by weight for each region.
   *
   * @param blocks an array of blocks or null.
   * @param regions an array of regions.
   *
   * @returns an object with the blocks grouped by region.
   */
  filterBlocksByRegions: (blocks: (DrupalBlock | null)[], regions: string[]) => {
    const blocksByRegions: Record<string, DrupalBlock[]> = {};

    regions.forEach((region) => {
      const filteredBlocks = blocks.filter(notEmpty);
      const regionBlocks = filteredBlocks.filter((block) => block.region === region);
      blocksByRegions[region] = PageBuilder.sortBlocksByWeight(regionBlocks);
    });

    return blocksByRegions;
  },

  /**
   * Render a block based on its bundle.
   *
   * Drupal returns null when it can't normalize a block, so we need to handle it.
   *
   * @param block - A block payload from Drupal.
   *
   * @returns The rendered block.
   */
  renderBlock: (block: DrupalBlock) => {
    const Block = PageBuilder.blocks[block.bundle];
    if (!Block) return null;

    const id = generateId(`${block.bundle}-${block.uuid}`);

    // Determine if the block should be animated based on its `supportsAnimation` flag and payload.
    const animate = Block.supportsAnimation ?? block.animate;

    return (
      <BlockWrapper animate={animate} attributes={block.attributes} key={id}>
        <Block {...block} />
      </BlockWrapper>
    );
  },

  /**
   * Render a node based on its bundle.
   *
   * @param node - A node payload from Drupal.
   *
   * @returns The rendered node.
   */
  renderNode: (node: DrupalNode) => {
    const Node = PageBuilder.nodes[node.bundle];
    if (!Node) return null;

    const id = generateId(`${node.bundle}-${node.id}`);

    return <Node {...node} key={id} />;
  },

  /**
   * Render a term based on its bundle.
   *
   * @param term - A term payload from Drupal.
   *
   * @returns The rendered term.
   */
  renderTerm: (term: DrupalTerm) => {
    const Term = PageBuilder.terms[term.bundle];
    if (!Term) return null;

    const id = generateId(`${term.bundle}-${term.id}`);

    return <Term {...term} key={id} />;
  },

  /**
   * Render a view based on its id.
   *
   * @param view - A view payload from Drupal.
   *
   * @returns The rendered view.
   */
  renderView: (view: DrupalView) => {
    const View = PageBuilder.views[view.id] || null;
    if (!View) return null;

    const id = generateId(`${view.id}-${view.display}`);

    return <View {...view} key={id} />;
  },

  /**
   * Render a paragraph based on its bundle.
   *
   * @param paragraph - A paragraph payload from Drupal.
   *
   * @returns The rendered paragraph.
   */
  renderParagraph: (paragraph: DrupalParagraph) => {
    const Paragraph = PageBuilder.paragraphs[paragraph.bundle];
    if (!Paragraph) return null;

    const id = generateId(`${paragraph.bundle}-${paragraph.id}`);

    return <Paragraph {...paragraph} key={id} />;
  },

  /**
   * Render a webform based on its id.
   *
   * @param id - A Drupal webform id.
   * @param props - Additional props to pass to the webform component.
   *
   * @returns The rendered webform.
   */
  renderWebform: <T,>(id: string, props?: WebformProps & T) => {
    const Webform = PageBuilder.webforms[id];
    if (!Webform) return null;

    return <Webform {...props} key={id} />;
  },
};

export default PageBuilder;
